import ActionButton from 'components/ActionButton';
import BackButton from 'components/BackButton';
import ImageUploadComponent from 'components/ImageUploadComponent';
import { Loading } from 'components/loading/Loading';
import CustomLink from 'components/TableLink';
import ViewImageModal from 'components/ViewItemImageModal';
import { tableStyles } from 'css/tableStyles';
import React, { useEffect, useState } from 'react';
import DataTable from 'components/DataTable';
import { fetchCategoryGroupDetails, updateCategoryGroup, updateCategoryGroupImage } from 'services';
import { showError, showSuccess } from 'utils';
import styles from './index.module.scss';
import _ from 'lodash';
import DynamicLink from 'components/DynamicLink';

const columns = [
  {
    name: 'Category ID',
    selector: 'objectId',
    // eslint-disable-next-line react/display-name
    cell: row => (
      <CustomLink
        text={row.objectId}
        linkTo={`/category/${row.objectId}`}
      />
    ),
  },
  {
    name: 'Name',
    selector: 'description',
    width: '220px',
    // eslint-disable-next-line react/display-name
    cell: row => (
      <div className={styles['category-name']} onClick={() => window.location.assign(`/category/${row.objectId}`)}>
        {row.image && (
          <img src={row.image?.url} alt={row.description} />
        )}
        <div>{row.description || '-'}</div>
      </div>
    ),
  },
  {
    name: 'Active',
    selector: 'active',
    format: row => row.active?.toString() || 'N/A',
  },
  {
    name: 'Tax Percent',
    selector: 'taxPercent',
  },
  {
    name: 'Storage Class',
    selector: 'storageClass',
  },
  {
    name: 'Sort',
    selector: 'sort',
  },
];

const CategoryGroupDetail = props => {
  const [loading, setLoading] = useState<boolean>(false);
  const [group, setGroup] = useState<Record<string, any>>({});
  const [updatedGroup, setUpdatedGroup] = useState<Record<string, any>>({});
  const [viewImageModalOpen, setViewImageModalOpen] = useState<boolean>(false);
  const [editBasicInfo, setEditBasicInfo] = useState<boolean>(false);
  const [editSEOFields, setEditSEOFields] = useState<boolean>(false);
  const [updateLoading, setUpdateLoading] = useState<boolean>(false);
  const [seoUpdateLoading, setSEOUpdateLoading] = useState<boolean>(false);
  const { categoryGroupId } = props.match.params;

  const fetchGroupDetail = async () => {
    setLoading(true);
    try {
      const response = await fetchCategoryGroupDetails(categoryGroupId);
      setGroup(response);
      setUpdatedGroup(response);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroupDetail();
  }, []);
  
  const handleUpdate = async (payload: Record<string, any>) => {
    setUpdateLoading(true);
    const data = {
      ...payload,
      id: categoryGroupId,
    };
    try {
      await updateCategoryGroup(data);
      showSuccess('Category Group updated successfully');
    } catch (e) {
      showError(e);
    } finally {
      setEditBasicInfo(!editBasicInfo);
      setUpdateLoading(false);
    }
  };

  const handleEditCancel = (fields: string[], isSeo: boolean) => {
    setUpdatedGroup({
      ...updatedGroup,
      ..._.pick(updatedGroup, fields),
    });
    if (isSeo) {
      setEditSEOFields(false);
    } else {
      setEditBasicInfo(false);
    }
  };
  
  const handleSEOPrimaryAction = async () => {
    if (editSEOFields) {
      setSEOUpdateLoading(true);
      await handleUpdate({
        name: updatedGroup.name,
        enName: updatedGroup.enName,
        metaTitle: updatedGroup.metaTitle,
        metaDescription: updatedGroup.metaDescription,
        enMetaTitle: updatedGroup.enMetaTitle,
        enMetaDescription: updatedGroup.enMetaDescription,
      });
      setSEOUpdateLoading(false);
    }
    setEditSEOFields(!editSEOFields);
  };
  
  const handleEdit = (key: string, value: string | number) => {
    setUpdatedGroup({
      ...updatedGroup,
      [key]: value,
    });
  };

  const getCategories = () => (
    <div>
      <div className={styles['subheading']}>Categories</div>
      <DataTable
        data={group.categories}
        columns={columns}
        customStyles={tableStyles}
        noHeader={true}
      />
    </div>
  );
  
  const EditableDetailsGroup = (
    {
      label,
      detail = '',
      isTextArea = false,
      propertyKey,
    }:
    {
      label: string;
      detail: string;
      isTextArea?: boolean;
      propertyKey: string;
    },
  ) => (
    <div className={styles['info-group']}>
      <div className={styles['label']}>{label}</div>
      {editSEOFields ? (
        isTextArea ? (
          <textarea
            key={propertyKey}
            disabled={seoUpdateLoading}
            className={'form-control'}
            value={detail}
            onChange={e => handleEdit(propertyKey, e.target.value)}
          />
        ) : (
          <input
            key={propertyKey}
            disabled={seoUpdateLoading}
            className={'form-control'}
            value={detail}
            onChange={e => handleEdit(propertyKey, e.target.value)}
          />
        )
      ) : (
        <div className={styles['info']}>{detail || '-'}</div>
      )}
    </div>
  );

  const getSEOFields = () => (
    <div className={styles['seo-section']}>
      <div className={styles['section-header']}>
        <div className={styles['section-heading']}>SEO Fields</div>
        <div className={'d-flex'}>
          <ActionButton
            text={editSEOFields ? 'Save' : 'Edit'}
            active={true}
            isLoading={false}
            onClick={handleSEOPrimaryAction}
          />
          {editSEOFields && (
            <ActionButton
              text={'Cancel'}
              active={true}
              isLoading={false}
              onClick={() => handleEditCancel([], true)}
              customClass={styles['cancel-button']}
            />
          )}
        </div>
      </div>
      <div className={styles['fields-section']}>
        {EditableDetailsGroup({
          detail: updatedGroup.name,
          label: 'Spanish Name',
          propertyKey: 'name',
        })}
        {EditableDetailsGroup({
          detail: updatedGroup.enName,
          label: 'English Name',
          propertyKey: 'enName',
        })}
        {EditableDetailsGroup({
          detail: updatedGroup.metaTitle,
          label: 'Spanish Meta Title',
          propertyKey: 'metaTitle',
        })}
        {EditableDetailsGroup({
          detail: updatedGroup.enMetaTitle,
          label: 'English Meta Title',
          propertyKey: 'enMetaTitle',
        })}
        {EditableDetailsGroup({
          detail: updatedGroup.metaDescription,
          label: 'Spanish Meta Description',
          isTextArea: true,
          propertyKey: 'metaDescription',
        })}
        {EditableDetailsGroup({
          detail: updatedGroup.enMetaDescription,
          label: 'English Meta Description',
          isTextArea: true,
          propertyKey: 'enMetaDescription',
        })}
      </div>
    </div>
  );
  
  const getBasicDetailsSection = () => (
    <div className={styles['basic-details-section']}>
      <div className={styles['image-container']}>
        <ImageUploadComponent
          src={group.image?._url}
          entityId={group.objectId}
          toggleModal={() => setViewImageModalOpen(!viewImageModalOpen)}
          uploadAPI={updateCategoryGroupImage}
        />
      </div>
      <div className={styles['info-container']}>
        <div className={styles['info-section']}>
          <div className={styles['info-group']}>
            <div className={styles['label']}>Category Group ID</div>
            <div className={styles['info']}>{group.objectId}</div>
          </div>
          <div className={styles['info-group']}>
            <div className={styles['label']}>Name</div>
            <div className={styles['info']}>{group.name}</div>
          </div>
          <div className={styles['info-group']}>
            <div className={styles['label']}>Sort</div>
            {editBasicInfo ? (
              <input
                className={'form-control'}
                disabled={updateLoading}
                value={updatedGroup.sort}
                onChange={e => handleEdit('sort', parseInt(e.target.value))}
              />
            ) : (
              <div className={styles['info']}>{updatedGroup.sort}</div>
            )}
          </div>
          <DynamicLink link={group?.dynamicLink}/>
        </div>
        <div className={styles['action-section']}>
          {editBasicInfo ? (
            <div className={'d-flex'}>
              <ActionButton
                active={true}
                isLoading={false}
                onClick={() => handleUpdate({
                  sort: updatedGroup.sort,
                })}
                text={'Save'}
              />
              <ActionButton
                active={true}
                isLoading={false}
                onClick={() => handleEditCancel(['name', 'sort'], false)}
                text={'Cancel'}
                customClass={styles['cancel-button']}
              />
            </div>
          ) : (
            <div
              className={styles['edit']}
              onClick={() => setEditBasicInfo(!editBasicInfo)}
            >
              Edit
            </div>
          )}
        </div>
      </div>
    </div>
  );
  
  const getHeaderSection = () => (
    <div className={styles['header']}>
      <div className={styles['heading']}>Category Group Details</div>
      <div className={styles['buttons-section']}>
        <BackButton />
        <ActionButton
          text={<>Category<span>+</span></>}
          active={true}
          isLoading={false}
          onClick={() => props.history.push(`/category/create/${categoryGroupId}`)}
        />
      </div>
    </div>
  );

  if (loading) return <Loading />;
  
  return (
    <>
      {
        viewImageModalOpen && (
          <ViewImageModal
            originalAspect={true}
            source={group.image?._url}
            toggleModal={() => setViewImageModalOpen(!viewImageModalOpen)}
          />
        )
      }
      <div className={`${styles['main-container']}`}>
        {getHeaderSection()}
        {getBasicDetailsSection()}
        {getSEOFields()}
        {getCategories()}
      </div>
    </>
  );
};

export { CategoryGroupDetail };